import { useState, useCallback, useEffect, useRef } from "react";
import { useHostAuth } from "./hooks/useHostAuth";
import { useHostConfig } from "./hooks/useHostConfig";
import MfeIframeLoader from "./components/MfeIframeLoader";

function App() {
  const { currentToken, getAuthTokenForMfe, refreshToken, uniqueIdentifier } =
    useHostAuth();
  const { getApiUrl, getMfeUiLayerUrl } = useHostConfig();
  const [integrationMode] = useState<"mf" | "iframe">("iframe");
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const EXPECTED_MFE_ORIGIN = new URL(getMfeUiLayerUrl("iframe")).origin;

  const handleMfeEvent = useCallback(
    async (eventType: string, payload?: any) => {
      console.log(`[Host] MFE emitted event: ${eventType}`, payload);
      switch (eventType) {
        case "authTokenExpired":
          console.log(
            "[Host] MFE indicated auth expired. Attempting refresh..."
          );
          refreshToken().then((newToken) => {
            if (
              newToken &&
              integrationMode === "iframe" &&
              iframeRef.current?.contentWindow
            ) {
              console.log("[Host] Sending refreshed token to MFE (iframe)...");
              iframeRef.current.contentWindow.postMessage(
                {
                  type: "authTokenUpdated",
                  payload: { token: newToken },
                },
                getMfeUiLayerUrl("iframe")
              ); // Target specific origin
            } else if (newToken && integrationMode === "mf") {
              console.log(
                "[Host] Token refreshed. MF MFE will pick it up on next call to getAuthToken."
              );
            }
          });
          break;
        case "requestAuthToken":
          console.log("[Host] MFE requested auth token.");
          let token = currentToken;
          if (!token) {
            token = await getAuthTokenForMfe();
          }
          if (
            integrationMode === "iframe" &&
            iframeRef.current?.contentWindow
          ) {
            iframeRef.current.contentWindow.postMessage(
              {
                type: "authTokenUpdated",
                payload: { token: token },
              },
              getMfeUiLayerUrl("iframe")
            );
          }
          break;
        case "someOtherMfeEvent":
          // Handle other events
          break;
        default:
          console.log(`[Host] Unhandled event type: ${eventType}`);
          break;
      }
    },
    [refreshToken, integrationMode, getMfeUiLayerUrl]
  );

  // --- Communication TO Iframe MFE (e.g., for initial config or updates) ---
  const sendInitialConfigToIframe = useCallback(() => {
    if (!uniqueIdentifier) {
      console.log(
        "[Host] Waiting for uniqueIdentifier before sending config..."
      );
      return;
    }
    if (integrationMode === "iframe" && iframeRef.current?.contentWindow) {
      console.log("[Host] Sending initial config to MFE (iframe)...");
      console.log("iframeRef", iframeRef.current?.contentWindow);
      iframeRef.current.contentWindow.postMessage(
        {
          type: "initialConfiguration",
          payload: {
            apiBaseUrl: getApiUrl(),
            initialToken: currentToken,
            initialPath: "/",
            uniqueIdentifier,
          },
        },
        getMfeUiLayerUrl("iframe")
      );
    }
  }, [
    integrationMode,
    getApiUrl,
    currentToken,
    getMfeUiLayerUrl,
    uniqueIdentifier,
  ]);

  // Listen for messages FROM iframe MFE
  useEffect(() => {
    const handleMessageFromMfe = (event: MessageEvent) => {
      console.log("[Host] Message received from MFE:", event);
      console.log("[Host] iFrame URL:", getMfeUiLayerUrl("iframe"));

      // --- VALIDATION STEP 1: Check the origin ---
      if (event.origin !== EXPECTED_MFE_ORIGIN) {
        console.warn(
          `[Host] Ignored message from untrusted origin. Expected: ${EXPECTED_MFE_ORIGIN}, Received: ${event.origin}`
        );
        return; // Do not process the message
      }

      // --- VALIDATION STEP 2: Check the source window (optional but good for iframes) ---
      // Ensure the message came from the contentWindow of your specific iframe.
      // This is useful if the host might have multiple iframes.
      if (
        iframeRef.current &&
        event.source !== iframeRef.current.contentWindow
      ) {
        console.warn(
          "[Host] Ignored message: Source does not match the expected MFE iframe window."
        );
        return;
      }

      const { type, payload } = event.data;
      console.log("[Host] Received message from MFE (iframe):", type, payload);
      handleMfeEvent(type, payload);
    };

    window.addEventListener("message", handleMessageFromMfe);
    return () => {
      window.removeEventListener("message", handleMessageFromMfe);
    };
  }, [getAuthTokenForMfe, getMfeUiLayerUrl]);

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <h1 style={{ margin: "20px 0 0 0" }}>Parcel Host Application</h1>
      <hr style={{ margin: "20px 0" }} />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <MfeIframeLoader
          ref={iframeRef}
          src={`${getMfeUiLayerUrl("iframe")}`}
          title="User MFE (Iframe)"
          onLoad={sendInitialConfigToIframe}
        />
      </div>
    </div>
  );
}

export default App;
